import { minBy, uniqWith } from 'es-toolkit';

import {
  circularPairwise,
  circularTripletwise,
  groupAdjacentElementsCircularly,
  hypotenuse2D,
  notNullish,
  pairwise,
  sortBy,
} from '@paragon/utilities';

import {
  type AxisAlignedBounds,
  boundsContainPoint,
  boundsContainPointWithinDistance,
  boundsOverlap,
} from './AxisAlignedBounds';
import Direction2D from './Direction2D';
import Direction3D from './Direction3D';
import Line2D from './Line2D';
import LineSegment2D from './LineSegment2D';
import Point2D from './Point2D';
import Polygon3D, { getNormalDirection, tryGetNormalDirection } from './Polygon3D';
import { areConnectedSegmentsSelfIntersecting } from './polygonValidationUtilities';
import { angleEquals, distanceGreaterThanOrEqual } from './units';
import Vector2D from './Vector2D';

class Polygon2D {
  public readonly vertices: readonly Point2D[];
  public readonly edges: readonly LineSegment2D[];

  private _centroid: Point2D | null = null;

  private _area: number | null = null;

  private _size: { width: number; height: number } | null = null;

  private _axisAlignedBounds: AxisAlignedBounds | null = null;

  public get area(): number {
    if (this._area === null) {
      this._area = this.findArea();
    }
    return this._area;
  }

  public get centroid(): Point2D {
    if (this._centroid === null) {
      this._centroid = this.findCenter();
    }
    return this._centroid;
  }

  public get size(): { width: number; height: number } {
    if (this._size === null) {
      this._size = this.findSize();
    }
    return this._size;
  }

  private get axisAlignedBounds(): AxisAlignedBounds {
    if (this._axisAlignedBounds === null) {
      let minX = Infinity;
      let maxX = -Infinity;
      let minY = Infinity;
      let maxY = -Infinity;

      for (const { x, y } of this.vertices) {
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
      }

      this._axisAlignedBounds = {
        minX,
        maxX,
        minY,
        maxY,
      };
    }
    return this._axisAlignedBounds;
  }

  private axisAlignedBoundsOverlap(other: Polygon2D): boolean {
    return boundsOverlap(this.axisAlignedBounds, other.axisAlignedBounds);
  }

  /**
   * A cheap, conservative check for whether a segment can intersect this polygon. A false result guarantees there is
   * no intersection; a true result only means that the segment and polygon have overlapping axis-aligned bounds.
   */
  private mayIntersectSegment(segment: LineSegment2D): boolean {
    return boundsOverlap(this.axisAlignedBounds, segment.axisAlignedBounds);
  }

  /**
   * Splits the segment at every point where it crosses this polygon's edges. Returns the segment unchanged when it
   *   cannot intersect the polygon.
   */
  public splitSegmentAtEdges(segment: LineSegment2D): LineSegment2D[] {
    if (!this.mayIntersectSegment(segment)) return [segment];

    const intersections = this.edges.map((edge) => segment.intersectWithSegment(edge)).filter(notNullish);
    return segment.splitAtPointsKnownToBeOnSegment(intersections);
  }

  private axisAlignedBoundsContainPoint(point: Point2D): boolean {
    return boundsContainPoint(this.axisAlignedBounds, point);
  }

  /**
   * Returns the min and max projections of vertices onto the given direction, measured from the centroid. Useful for
   *   finding the extent of a polygon along an arbitrary axis.
   */
  public projectionBoundsFromCentroid(direction: Direction2D): [number, number] {
    const projections = this.vertices.map((vertex) =>
      Vector2D.fromPointAndPoint(this.centroid, vertex).getComponentInDirection(direction),
    );
    return [Math.min(...projections), Math.max(...projections)];
  }

  constructor(unorientedPoints: readonly Point2D[], validateOrientation = true) {
    if (unorientedPoints.length < 3) throw new Error('A Polygon should have at least three points');

    this.vertices = validateOrientation ? orientPointsCounterClockwise(unorientedPoints) : unorientedPoints;

    const edges: LineSegment2D[] = [];
    for (let i = 0; i < this.vertices.length; i += 1) {
      edges.push(new LineSegment2D(this.vertices[i], this.vertices[(i + 1) % this.vertices.length]));
    }

    this.edges = edges;

    // TODO Add validation to make sure that the passed points actually form a polygon.
  }

  static fromVerticesIfValid(vertices: readonly Point2D[]) {
    if (vertices.length < 3) return null;
    if (!circularPairwise(vertices).every(([vertex1, vertex2]) => !vertex1.equals(vertex2))) return null;
    if (!tryGetNormalDirection(vertices.map((point) => point.to3D()))) return null;
    if (
      areConnectedSegmentsSelfIntersecting(
        circularPairwise(vertices).map(([basePoint, endPoint]) => new LineSegment2D(basePoint, endPoint)),
      )
    )
      return null;
    return new Polygon2D(vertices);
  }

  static fromOrderedSegmentsIfValid(segments: readonly LineSegment2D[]) {
    return circularPairwise(segments).every(([segment1, segment2]) => segment1.endPoint.equals(segment2.basePoint))
      ? Polygon2D.fromVerticesIfValid(segments.map(({ basePoint }) => basePoint))
      : null;
  }

  static removeCollinearPoints(points: readonly Point2D[]): readonly Point2D[] {
    if (points.length < 3) return points;

    const newPoints = [];
    for (let i = 0; i < points.length; i++) {
      const basePoint = points[(i + points.length - 1) % points.length];
      const pointToCheck = points[i];
      const endPoint = points[(i + points.length + 1) % points.length];

      if (!basePoint.directionToPoint(pointToCheck).isParallelTo(pointToCheck.directionToPoint(endPoint)))
        newPoints.push(pointToCheck);
    }

    return newPoints;
  }

  public static removeIntermediateCollinearPoints(points: readonly Point2D[]): readonly Point2D[] {
    if (points.length < 3) return points;

    return circularTripletwise(points)
      .map(([previous, current, next]) =>
        previous.directionToPoint(current).equals(current.directionToPoint(next)) ? null : current,
      )
      .filter(notNullish);
  }

  public equals(other: Polygon2D) {
    if (this.edges.length !== other.edges.length) {
      return false;
    }
    const { vertices } = this;
    const offset = other.vertices.findIndex((vertex) => vertex.equals(vertices[0]));
    if (offset < 0) {
      return false;
    }
    const tester = (vertex: Point2D, index: number) => vertices[index + 1].equals(vertex);
    const rotatedOther = [...other.vertices.slice(offset + 1), ...other.vertices.slice(0, offset)];
    const reversedOtherRotated = rotatedOther.toReversed();
    return rotatedOther.every(tester) || reversedOtherRotated.every(tester);
  }

  private sliceInternal(
    slicingLine: Line2D,
    computeStarboard = true,
  ): {
    port: Polygon2D[];
    starboard: Polygon2D[];
  } {
    const halfPlane = slicingLine.getPortSideHalfPlane();
    const [insideEdges, outsideEdges]: (LineSegment2D | null)[][] = [[], []];
    for (const edge of this.edges) {
      if (
        (halfPlane.containsPointOnInside(edge.basePoint) && halfPlane.containsPointOnOutside(edge.endPoint)) ||
        (halfPlane.containsPointOnInside(edge.endPoint) && halfPlane.containsPointOnOutside(edge.basePoint))
      ) {
        const splitEdge = edge.slice(halfPlane);
        insideEdges.push(splitEdge.sliceInHalfPlane!);
        computeStarboard && outsideEdges.push(splitEdge.sliceOutsideHalfPlane!);
      } else if (edge.endPoints.some(halfPlane.containsPointOnInside.bind(halfPlane))) {
        insideEdges.push(edge.slice(halfPlane).sliceInHalfPlane!);
      } else if (edge.endPoints.some(halfPlane.containsPointOnOutside.bind(halfPlane))) {
        computeStarboard && outsideEdges.push(edge.slice(halfPlane).sliceOutsideHalfPlane!);
      }
    }
    const createPolygons = (edges: readonly (LineSegment2D | null)[], newEdgeDirection: Direction2D): Polygon2D[] => {
      const edgeSequences = groupAdjacentElementsCircularly(
        edges.filter(notNullish),
        (edge1) => !slicingLine.containsPoint(edge1.endPoint),
      )
        .map((edgeSequence) => ({
          edgeSequence,
          component:
            edgeSequence[edgeSequence.length - 1].endPoint.vectorFromOrigin.getComponentInDirection(newEdgeDirection),
        }))
        .sort((a, b) => a.component - b.component)
        .map(({ edgeSequence }) => edgeSequence);
      const result: (Polygon2D | null)[] = [];
      let unusedEdgeSequences = [...edgeSequences];
      for (const startingEdgeSequence of edgeSequences) {
        if (!unusedEdgeSequences.includes(startingEdgeSequence)) continue;
        const polygonEdges: LineSegment2D[] = [];
        let previousPoint = startingEdgeSequence[startingEdgeSequence.length - 1].endPoint;
        while (true) {
          const [loopPreviousPoint, loopUnusedEdgeSequences] = [previousPoint, unusedEdgeSequences];
          const nextEdgeSequence = minBy(
            edgeSequences
              .filter((sequence) => loopUnusedEdgeSequences.some((unusedSequence) => unusedSequence === sequence))
              .filter((edgeSequence) =>
                distanceGreaterThanOrEqual(
                  edgeSequence[0].basePoint
                    .subtract(loopPreviousPoint)
                    .vectorFromOrigin.getComponentInDirection(newEdgeDirection),
                  0,
                ),
              ),
            (edgeSequence) => edgeSequence[0].basePoint.vectorFromOrigin.getComponentInDirection(newEdgeDirection),
          )!;
          const nextPoint = nextEdgeSequence[0].basePoint;
          if (!previousPoint.equals(nextPoint)) polygonEdges.push(new LineSegment2D(previousPoint, nextPoint));
          polygonEdges.push(...nextEdgeSequence);
          unusedEdgeSequences = unusedEdgeSequences.filter((sequence) => sequence !== nextEdgeSequence);
          if (nextEdgeSequence === startingEdgeSequence) break;
          previousPoint = nextEdgeSequence[nextEdgeSequence.length - 1].endPoint;
        }
        result.push(Polygon2D.fromOrderedSegmentsIfValid(polygonEdges));
      }
      return result.filter(notNullish);
    };
    return {
      port: createPolygons(insideEdges, slicingLine.direction),
      starboard: computeStarboard ? createPolygons(outsideEdges, slicingLine.direction.reverse()) : [],
    };
  }

  slice(slicingLine: Line2D, oriented = false): Polygon2D[] {
    const { port, starboard } = this.sliceInternal(slicingLine, !oriented);
    return port.concat(starboard);
  }

  translate(translation: Vector2D): Polygon2D {
    return new Polygon2D(this.vertices.map((vertex) => vertex.translate(translation)));
  }

  /**
   * Enlarges this polygon by shifting all edges outwards by the given distance.
   *
   * Notes:
   *   - Can only be used with convex polygons.
   *   - Polygons with collinear edges will have those edges merged.
   */
  inflate(distance: number) {
    return new Polygon2D(
      circularPairwise(this.edges)
        .map(([edge1, edge2]) => {
          if (edge1.direction.equals(edge2.direction)) return null;

          const vector1 = edge1.direction.toVector(distance);
          const vector2 = edge2.direction.toVector(-distance);
          const commonEndPoint = edge1.endPoint;

          return commonEndPoint.translate(vector1).translate(vector2);
        })
        .filter(notNullish),
    );
  }

  /**
   * Calculates the line segment(s) of the intersection between this polygon and the given line. Can be used with
   *   non-convex polygons.
   */
  findSegmentIntersectionsWithLine(line: Line2D): LineSegment2D[] {
    const edgeIntersections = this.edges.map((edge) => edge.intersectWithLine(line)).filter(notNullish);
    const uniqueIntersections = uniqWith(edgeIntersections, (a, b) => a.equals(b));
    const sortedIntersections = sortBy(uniqueIntersections, (point) => point.signedDistanceAlong(line));

    const segments = pairwise(sortedIntersections).map(([pointA, pointB]) => new LineSegment2D(pointA, pointB));
    return segments.filter((segment) => this.containsPoint(segment.midpoint));
  }

  hasSegmentIntersectionWithLine(line: Line2D): boolean {
    const halfPlane = line.getPortSideHalfPlane();
    const passesThroughInterior =
      this.vertices.some((vertex) => halfPlane.containsPointOnInside(vertex)) &&
      this.vertices.some((vertex) => halfPlane.containsPointOnOutside(vertex));

    return passesThroughInterior || this.edges.some((edge) => line.containsSegment(edge));
  }

  /**
   * Checks if the point is on any of the boundaries of the polygon.
   */
  containsPointOnBoundary(point: Point2D) {
    return this.edges.some((edge) => edge.contains(point));
  }

  /**
   * Checks if the point is contained within this polygon, excluding its boundaries.
   */
  containsPointOnInside(point: Point2D) {
    return (
      this.axisAlignedBoundsContainPoint(point) &&
      !this.containsPointOnBoundary(point) &&
      this.containsPointInternalImplementation(point)
    );
  }

  /**
   * Returns true if the point is inside the polygon, including on its boundaries.
   */
  containsPoint(point: Point2D) {
    return (
      this.axisAlignedBoundsContainPoint(point) &&
      (this.containsPointOnBoundary(point) || this.containsPointInternalImplementation(point))
    );
  }

  /**
   * Returns true if the point is inside the polygon or within `distance` of its boundaries.
   *
   * **This function ignores tolerance to work with distances less than tolerance, but that also means that when
   *   `distance <= 0`, this will behave inconsistently with points on the boundaries. Do not use this function if
   *   `distance` is or could be zero.**
   */
  containsPointWithinDistance(point: Point2D, distance: number) {
    if (!boundsContainPointWithinDistance(this.axisAlignedBounds, point, distance)) {
      return false;
    }

    return (
      this.edges.some((edge) => point.distanceToLineSegment(edge) <= distance) ||
      this.containsPointInternalImplementation(point)
      // no boundary check because this function needs to support distances less than tolerance
    );
  }

  /**
   * Returns true if the passed polygons have at least one area of polygonal overlap.
   * Only defined for convex polygons. May return false for extremely small but valid overlaps.
   */
  static areOverlappingConvex(polygon1: Polygon2D, polygon2: Polygon2D): boolean {
    return (
      polygon1.axisAlignedBoundsOverlap(polygon2) &&
      overlappingSemiCheck(polygon1, polygon2) &&
      overlappingSemiCheck(polygon2, polygon1)
    );
  }

  touchesOrOverlaps(other: Polygon2D): boolean {
    if (!this.axisAlignedBoundsOverlap(other)) return false;

    // If any edge intersects with another, the polygons touch or overlap.
    if (this.edges.some((edge) => other.edges.some(edge.intersectWithSegment.bind(edge)))) return true;

    // Otherwise, the polygons only touch or overlap if one is contained within the other. Check any vertex on both
    // polygons for containment.
    if (this.containsPoint(other.vertices[0]) || other.containsPoint(this.vertices[0])) return true;

    return false;
  }

  /**
   * @deprecated This method is incomplete and inaccurate, only added for use by an experimental feature flag.
   * @see https://paragontruss.slack.com/archives/CDA3X0CEQ/p1725982780499319?thread_ts=1725973549.489719&cid=CDA3X0CEQ
   *        for correct implementation.
   */
  overlaps(other: Polygon2D): boolean {
    return (
      this.vertices.some(other.containsPointOnInside.bind(other)) ||
      other.vertices.some(this.containsPointOnInside.bind(this))
    );
  }

  /**
   * Given two polygons whose interiors do not overlap, find the single segment of overlap between their edges.
   */
  overlappingSegment(other: Polygon2D): LineSegment2D | null {
    const overlappingSegments = this.overlappingSegments(other);
    if (overlappingSegments.length > 1) {
      throw new Error('Found more than one overlapping segment.');
    }

    return overlappingSegments[0] ?? null;
  }

  overlappingSegments(other: Polygon2D): LineSegment2D[] {
    const overlappingSegments: LineSegment2D[] = [];

    for (const thisEdge of this.edges) {
      for (const otherEdge of other.edges) {
        if (thisEdge.direction.equals(otherEdge.direction.reverse())) {
          const overlappingSegment = thisEdge.overlappingSegment(otherEdge);
          if (overlappingSegment) {
            overlappingSegments.push(otherEdge);
          }
        }
      }
    }

    return overlappingSegments;
  }

  /**
   * Meant to only be called after checking whether the point is on a boundary or not since the behavior when the point
   * is on a boundary is undefined.
   */
  private containsPointInternalImplementation(point: Point2D) {
    let angleSum = 0;

    for (let i = 0; i < this.vertices.length; i++) {
      const current = this.vertices[i];
      const next = this.vertices[(i + 1) % this.vertices.length];

      const direction1 = new Direction2D(current.x - point.x, current.y - point.y);
      const direction2 = new Direction2D(next.x - point.x, next.y - point.y);

      const angle = direction1.angleTo(direction2);

      angleSum += angle;
    }

    angleSum %= 720;
    if (angleSum < 0) angleSum += 720;

    if (!angleEquals(angleSum, 0) && !angleEquals(angleSum, 360) && !angleEquals(angleSum, 720))
      throw new Error(`Expected angle sum to be either 0, 360, or 720 degrees, but found ${angleSum} degrees.`);

    return angleEquals(angleSum, 360);
  }

  private findArea(): number {
    let total = 0;
    for (let i = 1; i + 1 < this.vertices.length; i += 1) {
      const vector1 = Vector2D.fromPointAndPoint(this.vertices[0], this.vertices[i]);
      const vector2 = Vector2D.fromPointAndPoint(this.vertices[0], this.vertices[i + 1]);
      total += perpendicularDotProduct(vector1, vector2);
    }
    return Math.abs(total) / 2;
  }

  private findCenter(): Point2D {
    let total = Vector2D.zero;

    for (let i = 2; i < this.vertices.length; i += 1) {
      const vector1 = Vector2D.fromPointAndPoint(this.vertices[0], this.vertices[i - 1]);
      const vector2 = Vector2D.fromPointAndPoint(this.vertices[0], this.vertices[i]);
      const perpendicularDotProductResult = perpendicularDotProduct(vector1, vector2);
      const centroidOfLocalTriangle = centerPoint2D([this.vertices[0], this.vertices[i - 1], this.vertices[i]]);
      total = total.add(centroidOfLocalTriangle.vectorFromOrigin.multiplyScalar(perpendicularDotProductResult));
    }

    return total.divideScalar(2 * this.area).imageOfOrigin;
  }

  private findSize() {
    const { minX, maxX, minY, maxY } = this.axisAlignedBounds;

    return { width: maxX - minX, height: maxY - minY };
  }

  to3D(z = 0): Polygon3D {
    return new Polygon3D(this.vertices.map((vertex) => vertex.to3D(z)));
  }
}

export function centerPoint2D(points: Point2D[]) {
  return points.reduce((sum, current) => sum.add(current.vectorFromOrigin), Vector2D.zero).divideScalar(points.length)
    .imageOfOrigin;
}

// http://mathworld.wolfram.com/PerpDotProduct.html
const perpendicularDotProduct = (v1: Vector2D, v2: Vector2D) => {
  const product1 = v1.x * v2.y;
  const product2 = v1.y * v2.x;
  return product1 - product2;
};

function orientPointsCounterClockwise(points: readonly Point2D[]) {
  return getNormalDirection(points.map((point) => point.to3D())).equals(Direction3D.out) ? points : points.toReversed();
}

function overlappingSemiCheck(polygon1: Polygon2D, polygon2: Polygon2D): boolean {
  return polygon1.edges.every((edge) => polygon2.vertices.some(pointIsOnInsideOfEdge(edge)));
}

function pointIsOnInsideOfEdge(edge: LineSegment2D): (point: Point2D) => boolean {
  // Find direction and length of edge
  const { basePoint, endPoint } = edge;
  const edgeDeltaX = endPoint.x - basePoint.x;
  const edgeDeltaY = endPoint.y - basePoint.y;
  const edgeLength = hypotenuse2D(edgeDeltaX, edgeDeltaY);

  // Find normal direction of edge
  const edgeNormalDirectionX = -edgeDeltaY / edgeLength;
  const edgeNormalDirectionY = edgeDeltaX / edgeLength;

  return ({ x, y }) => {
    // Project vector from edge basepoint to point along edge normal
    const pointDeltaX = x - basePoint.x;
    const pointDeltaY = y - basePoint.y;
    const projectionX = pointDeltaX * edgeNormalDirectionX;
    const projectionY = pointDeltaY * edgeNormalDirectionY;
    const projectionLength = projectionX + projectionY;

    // Compare to distance tolerance
    return distanceGreaterThanOrEqual(projectionLength, 0);
  };
}

export default Polygon2D;
